import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { X, TrendingUp } from 'lucide-react';

// ── Forecast projector ───────────────────────────────────────────────────────
// The full-screen "actual forecast" behind the deck's "The numbers" scene:
// tenanted per-crew pricing, one operator's yearly value, unit economics, and
// ARR scenarios — interactive, so the presenter drags the crew count to match
// the room's own base ("a 30-crew operator ≈ £12.6k/yr").
// Styled light like the My Day film: white page, portal blue accents, green
// reserved for the money figures. Colours are explicit (not theme tokens) so
// the projector shows the same bright page whatever theme the admin runs.
const TIERS = [
  { max: 5, rate: 45 },
  { max: 15, rate: 40 },
  { max: 30, rate: 35 },
  { max: Infinity, rate: 30 },
];
const rateFor = (crews) => TIERS.find((t) => crews <= t.max).rate;
const LIFETIME_MONTHS = 36; // retention assumption behind the LTV figure
const gbp = (n) => `£${Math.round(n).toLocaleString('en-GB')}`;

const Stat = ({ label, value, sub, accent }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
    <p className="text-xs font-black uppercase tracking-[0.2em] text-slate-500">{label}</p>
    <p className={`mt-2 text-4xl font-black sm:text-5xl ${accent ? 'text-emerald-600' : 'text-slate-900'}`}>{value}</p>
    {sub && <p className="mt-1 text-sm text-slate-500">{sub}</p>}
  </div>
);

// `crews` (controlled) turns this into a read-only mirror — the projector
// window follows the presenter's slider; `onCrewsChange` reports local drags
// so the presenter side can broadcast them.
const ForecastProjector = ({ onClose, crews: crewsProp = null, onCrewsChange }) => {
  const [crewsState, setCrewsState] = useState(30);
  const crews = crewsProp ?? crewsState;
  const setCrews = (v) => { setCrewsState(v); onCrewsChange?.(v); };

  const f = useMemo(() => {
    const rate = rateFor(crews);
    const monthly = crews * rate;
    const yearly = monthly * 12;
    const ltv = monthly * LIFETIME_MONTHS;
    return { rate, monthly, yearly, ltv };
  }, [crews]);

  const scenarios = useMemo(
    () => [10, 30, 100].map((ops) => ({ ops, arr: ops * f.yearly })),
    [f.yearly],
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] overflow-y-auto bg-[#f5f7fb] p-6 text-slate-900 sm:p-10"
      role="dialog"
      aria-label="Tenanted platform forecast"
    >
      <div className="mx-auto max-w-5xl space-y-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.25em] text-blue-600">
              <TrendingUp className="h-4 w-4" /> Tenanted platform forecast
            </p>
            <h1 className="mt-2 text-3xl font-black sm:text-5xl" style={{ color: '#0f172a' }}>What this does at scale</h1>
          </div>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-300 bg-white p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
              aria-label="Close forecast"
            >
              <X className="h-6 w-6" />
            </button>
          )}
        </div>

        {/* One operator, sized to the room */}
        <div className="rounded-3xl border border-blue-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-sm font-bold uppercase tracking-widest text-slate-500">One operator on the platform</p>
              <p className="mt-1 text-5xl font-black text-slate-900 sm:text-6xl">{crews} <span className="text-2xl font-bold text-slate-400">crews</span></p>
            </div>
            <p className="text-lg text-slate-700">{gbp(f.rate)}<span className="text-sm text-slate-500">/crew/month at this tier</span></p>
          </div>
          {crewsProp === null && (
            <input
              type="range"
              min="1"
              max="100"
              value={crews}
              onChange={(e) => setCrews(Number(e.target.value))}
              className="mt-6 w-full accent-blue-600"
              aria-label="Number of crews"
            />
          )}
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            <Stat label="Per month" value={gbp(f.monthly)} />
            <Stat label="Per year" value={gbp(f.yearly)} accent />
            <Stat label="Lifetime value" value={gbp(f.ltv)} sub={`${LIFETIME_MONTHS / 12}-year retention · payback ≈ 3 months`} />
          </div>
        </div>

        {/* ARR scenarios at this crew size */}
        <div>
          <p className="mb-3 text-sm font-bold uppercase tracking-widest text-slate-500">
            Annual recurring revenue — operators of this size
          </p>
          <div className="grid gap-4 sm:grid-cols-3">
            {scenarios.map(({ ops, arr }) => (
              <Stat key={ops} label={`${ops} operators`} value={gbp(arr)} sub="ARR" accent={ops === 100} />
            ))}
          </div>
          <p className="mt-4 text-sm text-slate-500">
            Partner channel on top: white-label resellers bring their own operator base — same platform, co-branded, revenue-shared.
          </p>
        </div>

        <div className="grid gap-3 text-sm text-slate-500 sm:grid-cols-4">
          {TIERS.map((t, i) => {
            const from = i === 0 ? 1 : TIERS[i - 1].max + 1;
            const label = t.max === Infinity ? `${from}+ crews` : `${from}–${t.max} crews`;
            return (
              <div key={label} className={`rounded-xl border px-4 py-3 text-center ${crews >= from && crews <= t.max ? 'border-emerald-500/70 bg-emerald-50 font-semibold text-slate-900' : 'border-slate-200 bg-white'}`}>
                <span className="font-bold">{label}</span> · {gbp(t.rate)}/crew/mo
              </div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
};

export default ForecastProjector;
